import { supabase } from '@/lib/supabase';
import {
  AI_TOOLS_FALLBACK,
  invokeAiTool,
  type AiToolContext,
  type AiToolResult,
} from '@/lib/coreAiTools';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 78. INTÉGRATION AVEC ZIFEK AI · 79. RÈGLE ABSOLUE DE SÉCURITÉ
//
// Architecture imposée :
//   Zifek AI → AI Tool → Tool Gateway → Authorization → Zifek Core Service
//            → Business Module → Database
//
// Le modèle IA ne reçoit JAMAIS : database credentials, SQL, tables
// complètes, ni les données d'un autre tenant.
//
// RÈGLE ABSOLUE : aucune requête IA ne peut contourner authentication,
// authorization, tenant isolation, validation ou business rules. Même si un
// utilisateur demande « donne-moi les données privées de cette entreprise »,
// la passerelle refuse s'il n'a pas la permission.
// ─────────────────────────────────────────────────────────────

export interface GatewayPolicy {
  id: number;
  policy_key: string;
  label: string | null;
  stage: number | null;
  kind: string | null;
  enforced: boolean | null;
  description: string | null;
  sort_order: number | null;
}

export async function listGatewayPolicies(): Promise<GatewayPolicy[]> {
  const { data, error } = await supabase
    .from('core_ai_gateway_policies')
    .select('*')
    .order('stage', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as GatewayPolicy[];
}

/** Étapes du pipeline (ordre imposé). */
export const GATEWAY_FLOW: { key: string; label: string; icon: string }[] = [
  { key: 'ai', label: 'Zifek AI', icon: 'ri-robot-2-line' },
  { key: 'tool', label: 'AI Tool', icon: 'ri-tools-line' },
  { key: 'gateway', label: 'Tool Gateway', icon: 'ri-shield-keyhole-line' },
  { key: 'authorization', label: 'Authorization', icon: 'ri-lock-2-line' },
  { key: 'core_service', label: 'Zifek Core Service', icon: 'ri-stack-line' },
  { key: 'module', label: 'Business Module', icon: 'ri-apps-2-line' },
  { key: 'database', label: 'Database', icon: 'ri-database-2-line' },
];

/** Ce que le modèle IA ne reçoit jamais. */
export const NEVER_EXPOSED: { key: string; label: string; icon: string }[] = [
  { key: 'credentials', label: 'Database credentials', icon: 'ri-key-2-line' },
  { key: 'sql', label: 'SQL', icon: 'ri-code-line' },
  { key: 'tables', label: 'Tables complètes', icon: 'ri-table-line' },
  { key: 'cross_tenant', label: 'Données d’autres tenants', icon: 'ri-git-merge-line' },
];

const STAGES_ORDER = ['authentication', 'tenant_isolation', 'authorization', 'validation', 'business_rules'] as const;
export type GuardStage = typeof STAGES_ORDER[number];

export interface AiGuardInput {
  toolKey: string;
  params?: Record<string, unknown>;
  organizationId: number | null;
  tenantId: number | null;
  /** Identifiant du compte appelant ; null = anonyme. */
  userId: number | null;
  canAccess: (permission: string) => boolean;
  /** Outils privés : authentification requise (défaut true). */
  requireAuth?: boolean;
}

export interface AiGuardDecision {
  allowed: boolean;
  stage: GuardStage | 'ok';
  reason: string;
  requiredPermission: string | null;
}

async function requiredPermissionFor(toolKey: string): Promise<string | null> {
  try {
    const { data } = await supabase.from('core_ai_tools').select('required_permission, is_active').eq('tool_key', toolKey).maybeSingle();
    if (data) return (data as { required_permission: string | null }).required_permission ?? null;
  } catch { /* best-effort */ }
  return AI_TOOLS_FALLBACK.find((t) => t.tool_key === toolKey)?.required_permission ?? null;
}

/**
 * Applique la règle absolue de sécurité AVANT toute exécution : aucune étape
 * ne peut être contournée. Ne lève jamais.
 */
export async function guardAiRequest(input: AiGuardInput): Promise<AiGuardDecision> {
  const requireAuth = input.requireAuth !== false;

  // 1. Authentication
  if (requireAuth && !input.userId) {
    return { allowed: false, stage: 'authentication', reason: 'Authentification requise pour invoquer un outil privé.', requiredPermission: null };
  }

  // 2. Tenant isolation
  if (!input.organizationId) {
    return { allowed: false, stage: 'tenant_isolation', reason: 'Isolation tenant : impossible de résoudre l’organisation de l’appelant.', requiredPermission: null };
  }

  // 3. Authorization
  const required = await requiredPermissionFor(input.toolKey);
  if (required && !input.canAccess(required)) {
    return {
      allowed: false,
      stage: 'authorization',
      reason: `Accès refusé : la permission « ${required} » est requise. Les données privées de l’entreprise ne sont jamais exposées sans autorisation.`,
      requiredPermission: required,
    };
  }

  // 4. Validation
  if (!input.toolKey || typeof input.toolKey !== 'string') {
    return { allowed: false, stage: 'validation', reason: 'Validation : clé d’outil manquante.', requiredPermission: required };
  }

  // 5. Business rules
  const params = input.params || {};
  if (input.toolKey.startsWith('get_customer_') && (params.customer_id == null || params.customer_id === '')) {
    return { allowed: false, stage: 'business_rules', reason: 'Règle métier : un identifiant client valide est requis.', requiredPermission: required };
  }

  return { allowed: true, stage: 'ok', reason: 'Autorisé : isolation tenant, permission, validation et règles métier vérifiées.', requiredPermission: required };
}

export interface AiSecurityEvent {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  actor_user_id: number | null;
  tool_key: string | null;
  decision: string | null;
  reason: string | null;
  stage: string | null;
  path: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export async function logSecurityEvent(input: {
  tenantId: number | null;
  organizationId: number | null;
  actorUserId: number | null;
  toolKey: string;
  decision: 'allow' | 'deny';
  reason: string;
  stage: string;
  metadata?: Record<string, unknown> | null;
}): Promise<void> {
  try {
    await supabase.from('core_ai_security_events').insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      actor_user_id: input.actorUserId,
      tool_key: input.toolKey,
      decision: input.decision,
      reason: input.reason,
      stage: input.stage,
      path: `ai->${input.toolKey}->${input.stage}`,
      metadata: input.metadata ?? null,
    });
  } catch { /* la journalisation ne bloque jamais */ }
}

export async function listSecurityEvents(organizationId: number, limit = 80): Promise<AiSecurityEvent[]> {
  const { data, error } = await supabase
    .from('core_ai_security_events')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as AiSecurityEvent[];
}

export interface GatewayStats {
  total: number;
  allowed: number;
  denied: number;
  byStage: Record<string, number>;
}

export async function fetchSecurityStats(organizationId: number): Promise<GatewayStats> {
  const { data } = await supabase
    .from('core_ai_security_events')
    .select('decision, stage')
    .eq('organization_id', organizationId);
  const byStage: Record<string, number> = {};
  let allowed = 0;
  let denied = 0;
  (data || []).forEach((r) => {
    const row = r as { decision: string | null; stage: string | null };
    if (row.decision === 'allow') allowed += 1;
    else denied += 1;
    byStage[row.stage || 'unknown'] = (byStage[row.stage || 'unknown'] || 0) + 1;
  });
  return { total: (data || []).length, allowed, denied, byStage };
}

export interface GatewayRunResult {
  decision: AiGuardDecision;
  result: AiToolResult | null;
}

/**
 * Point d'entrée UNIQUE recommandé pour toute invocation IA. Applique la
 * garde de sécurité, journalise la décision, puis exécute l'outil dans le Core.
 */
export async function runThroughGateway(
  toolKey: string,
  params: Record<string, unknown>,
  ctx: AiToolContext,
): Promise<GatewayRunResult> {
  const decision = await guardAiRequest({
    toolKey,
    params,
    organizationId: ctx.organizationId || null,
    tenantId: ctx.tenantId,
    userId: ctx.userId ?? null,
    canAccess: ctx.canAccess,
    requireAuth: ctx.source !== 'public',
  });

  await logSecurityEvent({
    tenantId: ctx.tenantId,
    organizationId: ctx.organizationId || null,
    actorUserId: ctx.userId ?? null,
    toolKey,
    decision: decision.allowed ? 'allow' : 'deny',
    reason: decision.reason,
    stage: decision.stage,
    metadata: params,
  });

  if (!decision.allowed) {
    return { decision, result: null };
  }

  const result = await invokeAiTool(toolKey, params, ctx);
  return { decision, result };
}

export function securityStageMeta(stage: string | null | undefined): { label: string; className: string } {
  switch (stage) {
    case 'ok': return { label: 'Autorisé', className: 'bg-primary-100 text-primary-900' };
    case 'authentication': return { label: 'Non authentifié', className: 'bg-red-100 text-red-800' };
    case 'tenant_isolation': return { label: 'Isolation tenant', className: 'bg-red-100 text-red-800' };
    case 'authorization': return { label: 'Autorisation', className: 'bg-red-100 text-red-800' };
    case 'validation': return { label: 'Validation', className: 'bg-accent-100 text-accent-900' };
    case 'business_rules': return { label: 'Règle métier', className: 'bg-accent-100 text-accent-900' };
    default: return { label: stage || '—', className: 'bg-background-100 text-foreground-500' };
  }
}